import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";
import { IconTile } from "@/components/ui/card";
import { Muted, PageTitle } from "@/components/ui/typography";

const alertVariants = cva(
  "relative grid w-full grid-cols-[0_1fr] items-start gap-y-0.5 rounded-lg px-4 pt-[11px] pb-3 text-body ring-1 has-[>svg]:grid-cols-[calc(var(--spacing)*4)_1fr] has-[>svg]:gap-x-2.5 [&>svg]:mt-0.5 [&>svg]:size-4 [&>svg]:translate-y-0.5",
  {
    variants: {
      variant: {
        default: "bg-muted/50 text-foreground ring-foreground/10 [&>svg]:text-muted-foreground",
        info: "bg-primary/5 text-foreground ring-primary/20 [&>svg]:text-primary",
        warning:
          "bg-alert-warning-bg text-alert-warning-fg ring-alert-warning-edge [&>svg]:text-alert-warning-icon *:data-[slot=alert-description]:text-alert-warning-body",
        success:
          "bg-readiness-ready-bg text-readiness-ready-fg ring-readiness-ready-edge [&>svg]:text-readiness-ready-fg *:data-[slot=alert-description]:text-foreground/80",
        destructive:
          "bg-destructive/5 text-destructive ring-destructive/20 [&>svg]:text-destructive dark:bg-transparent *:data-[slot=alert-description]:text-destructive",
      },
      // The one notice a page leads with: the card's own solid surface and padding with a solid brand edge, so it
      // reads as the page's headline. Nothing in it is translucent (a tint looks bad on a grey background), whatever
      // the variant. An AlertMedia (a DateLeaf) stands to the left of everything else from sm, above it on a phone.
      // `centered`: an empty state, its icon above its words (no certificate found).
      size: {
        default: "",
        centered: "flex flex-col items-center gap-2 px-6 py-6 text-center [&>svg]:size-7 [&>svg]:translate-y-0",
        prominent:
          "grid-cols-1 gap-x-6 gap-y-4 rounded-2xl bg-card p-4 text-card-foreground ring-2 ring-primary sm:p-6 sm:has-[>[data-slot=alert-media]]:grid-cols-[auto_1fr]",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

function Alert({
  className,
  variant,
  size,
  ...props
}: React.ComponentProps<"div"> & VariantProps<typeof alertVariants>) {
  return (
    <div
      data-slot="alert"
      data-size={size ?? "default"}
      role="alert"
      className={cn("group/alert", alertVariants({ variant, size }), className)}
      {...props}
    />
  );
}

function AlertTitle({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-title"
      className={cn("col-start-2 line-clamp-1 min-h-4 font-medium tracking-tight", className)}
      {...props}
    />
  );
}

function AlertDescription({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-description"
      className={cn(
        "col-start-2 grid justify-items-start gap-1 text-small leading-relaxed text-muted-foreground [&_p]:leading-relaxed",
        // A prominent notice's body is the message itself: full width, body size, the foreground colour.
        "group-data-[size=prominent]/alert:col-start-1 group-data-[size=prominent]/alert:gap-4 group-data-[size=prominent]/alert:text-body group-data-[size=prominent]/alert:text-foreground",
        PROMINENT_BESIDE_MEDIA,
        className
      )}
      {...props}
    />
  );
}

/** Beside an AlertMedia, a prominent notice's header and body share the column to its right (from sm). */
const PROMINENT_BESIDE_MEDIA = "sm:group-has-[>[data-slot=alert-media]]/alert:col-start-2";

/**
 * A prominent notice's picture: a DateLeaf, left of the header and body from sm, above them on
 * a phone. It spans the notice's two rows, so the text beside it never wraps underneath.
 */
function AlertMedia({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div data-slot="alert-media" className={cn("sm:col-start-1 sm:row-span-2 sm:row-start-1", className)} {...props} />
  );
}

/**
 * A prominent notice's header: the card headers' icon tile (unless an AlertMedia stands beside
 * it), the title in the page-title role, and one muted line under it — the notice a page leads with
 * reads as that page's headline.
 */
function AlertHeader({
  icon,
  title,
  description,
  className,
}: {
  icon?: React.ComponentType<{ className?: string; strokeWidth?: number }>;
  title: React.ReactNode;
  description?: React.ReactNode;
  className?: string;
}) {
  return (
    <div data-slot="alert-header" className={cn("flex items-start gap-3", PROMINENT_BESIDE_MEDIA, className)}>
      {icon ? <IconTile icon={icon} /> : null}
      <div className="min-w-0">
        <PageTitle as="h2">{title}</PageTitle>
        {description ? <Muted>{description}</Muted> : null}
      </div>
    </div>
  );
}

export { Alert, AlertTitle, AlertDescription, AlertHeader, AlertMedia };
